import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Tabs,
  Tab,
} from '@mui/material';
import {
  OpenInNew as OpenInNewIcon,
  Reply as ReplyIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { getSupportRequests, updateSupportRequestStatus, sendSupportReply } from '@services/supportService';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open', color: 'error' },
  { value: 'in-progress', label: 'In Progress', color: 'warning' },
  { value: 'resolved', label: 'Resolved', color: 'success' },
];

const statusColor = (s) => STATUS_OPTIONS.find((o) => o.value === s)?.color || 'default';
const statusLabel = (s) => STATUS_OPTIONS.find((o) => o.value === s)?.label || s;

const formatDate = (ts) => {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return formatDistanceToNow(date, { addSuffix: true });
};

const formatFullDate = (ts) => {
  if (!ts) return '—';
  const date = ts.toDate ? ts.toDate() : new Date(ts);
  return date.toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    year: 'numeric', hour: 'numeric', minute: '2-digit',
  });
};

const AdminSupportPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0); // 0=All, 1=Open, 2=In Progress, 3=Resolved

  // Detail dialog
  const [selected, setSelected] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  // Reply form
  const [replyText, setReplyText] = useState('');
  const [replyStatus, setReplyStatus] = useState('open');
  const [sending, setSending] = useState(false);
  const [replyError, setReplyError] = useState('');
  const [replySuccess, setReplySuccess] = useState(false);

  // Status update (without reply)
  const [statusUpdating, setStatusUpdating] = useState(null); // requestId being updated

  const TAB_FILTERS = ['all', 'open', 'in-progress', 'resolved'];

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getSupportRequests();
      setRequests(data);
    } catch (err) {
      console.error(err);
      setError('Failed to load support requests.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (req) => {
    setSelected(req);
    setReplyText('');
    setReplyStatus(req.status);
    setReplyError('');
    setReplySuccess(false);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelected(null);
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) return;
    try {
      setSending(true);
      setReplyError('');
      await sendSupportReply(selected.id, replyText, replyStatus !== selected.status ? replyStatus : undefined);
      setReplySuccess(true);
      setReplyText('');
      await loadRequests();
      // Refresh selected with updated data
      setRequests((prev) => {
        const updated = prev.find((r) => r.id === selected.id);
        if (updated) setSelected(updated);
        return prev;
      });
    } catch (err) {
      console.error(err);
      setReplyError('Failed to send reply. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (req, newStatus) => {
    try {
      setStatusUpdating(req.id);
      await updateSupportRequestStatus(req.id, newStatus);
      setRequests((prev) => prev.map((r) => r.id === req.id ? { ...r, status: newStatus } : r));
      if (selected?.id === req.id) setSelected((s) => ({ ...s, status: newStatus }));
    } catch (err) {
      console.error(err);
    } finally {
      setStatusUpdating(null);
    }
  };

  const filtered = TAB_FILTERS[tabValue] === 'all'
    ? requests
    : requests.filter((r) => r.status === TAB_FILTERS[tabValue]);

  const counts = TAB_FILTERS.map((f) =>
    f === 'all' ? requests.length : requests.filter((r) => r.status === f).length
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold">Support Requests</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Review and respond to user support submissions
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton onClick={loadRequests} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: 2 }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
        >
          {['All', 'Open', 'In Progress', 'Resolved'].map((label, i) => (
            <Tab
              key={label}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {label}
                  <Chip label={counts[i]} size="small" sx={{ height: 18, fontSize: 11 }} />
                </Box>
              }
            />
          ))}
        </Tabs>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>From</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Replies</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No support requests found.
                  </TableCell>
                </TableRow>
              )}
              {!loading && filtered.map((req) => (
                <TableRow key={req.id} hover sx={{ cursor: 'pointer' }} onClick={() => handleOpen(req)}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">{req.name || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary">{req.email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {req.subject}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(req.submittedAt)}</Typography>
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <Select
                        value={req.status || 'open'}
                        onChange={(e) => handleStatusChange(req, e.target.value)}
                        disabled={statusUpdating === req.id}
                        sx={{ fontSize: 13 }}
                      >
                        {STATUS_OPTIONS.map((o) => (
                          <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={req.replies?.length || 0}
                      size="small"
                      color={req.replies?.length > 0 ? 'primary' : 'default'}
                      variant={req.replies?.length > 0 ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                    <Tooltip title="Open & Reply">
                      <IconButton size="small" onClick={() => handleOpen(req)}>
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Detail / Reply Dialog */}
      <Dialog open={dialogOpen} onClose={handleClose} maxWidth="md" fullWidth>
        {selected && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="h6" fontWeight="bold">{selected.subject}</Typography>
                <Chip label={statusLabel(selected.status)} color={statusColor(selected.status)} size="small" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                From {selected.name || 'Anonymous'} ({selected.email}) · {formatFullDate(selected.submittedAt)}
              </Typography>
            </DialogTitle>

            <DialogContent dividers>
              {/* Original message */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Original Message</Typography>
              <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.50', whiteSpace: 'pre-wrap', borderRadius: 2 }}>
                <Typography variant="body2">{selected.message}</Typography>
              </Paper>

              {/* Reply history */}
              {selected.replies?.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Reply History ({selected.replies.length})
                  </Typography>
                  {selected.replies.map((reply, i) => (
                    <Paper key={i} variant="outlined" sx={{ p: 2, mb: 1.5, borderLeft: 4, borderColor: 'primary.main', borderRadius: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" fontWeight="bold" color="primary">{reply.adminName}</Typography>
                        <Typography variant="caption" color="text.secondary">{formatFullDate(reply.sentAt)}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{reply.message}</Typography>
                    </Paper>
                  ))}
                  <Divider sx={{ my: 2 }} />
                </>
              )}

              {/* Reply form */}
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Send Reply</Typography>

              {replySuccess && (
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }} onClose={() => setReplySuccess(false)}>
                  Reply sent successfully.
                </Alert>
              )}
              {replyError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReplyError('')}>{replyError}</Alert>
              )}

              <TextField
                fullWidth
                multiline
                rows={5}
                placeholder="Type your reply here..."
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                disabled={sending}
                sx={{ mb: 2 }}
              />

              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Update Status</InputLabel>
                <Select
                  value={replyStatus}
                  label="Update Status"
                  onChange={(e) => setReplyStatus(e.target.value)}
                  disabled={sending}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleClose} disabled={sending}>Close</Button>
              <Button
                variant="contained"
                startIcon={sending ? <CircularProgress size={16} color="inherit" /> : <ReplyIcon />}
                onClick={handleSendReply}
                disabled={sending || !replyText.trim()}
              >
                {sending ? 'Sending…' : 'Send Reply'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default AdminSupportPage;
